import {Component, OnInit} from '@angular/core';
import {AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from "@angular/forms";
import {FileSelectEvent, FileUploadModule} from "primeng/fileupload";
import {NgClass, NgForOf, NgIf} from "@angular/common";
import {InputTextModule} from "primeng/inputtext";
import {ButtonModule} from "primeng/button";
import {DialogService, DynamicDialog, DynamicDialogRef} from "primeng/dynamicdialog";
import {TextareaModule} from "primeng/textarea";
import {BankService} from "../../../core/services/bank.service";
import {CustomDialogData} from "../../../core/models/custom-dialog.model";
import {ToastService} from "../../../core/services/toast.service";
import {IBank} from "../../../core/models/bank.model";
import {AvatarModule} from "primeng/avatar";
import {SelectModule} from "primeng/select";
import {countries} from "../../../../assets/countries";

@Component({
    selector: 'app-bank-details',
    imports: [
        // Modules
        FileUploadModule, ReactiveFormsModule, InputTextModule, ButtonModule, SelectModule,
        // Directives & Pipes
        NgIf, NgClass, TextareaModule, AvatarModule
    ],
    templateUrl: './bank-details.component.html',
    styleUrl: './bank-details.component.scss'
})
export class BankDetailsComponent implements OnInit {

  formGroup = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(2), Validators.maxLength(100)]
    }),
    swiftCode: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(/^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/), Validators.minLength(8), Validators.maxLength(11)]
    }),
    codeBank: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(3), Validators.maxLength(10)]
    }),
    telephone: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, this.phoneValidator.bind(this)]
    }),
    email: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.email]
    }),
    address: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.maxLength(200)]
    }),
    country: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required]
    }),
    description: new FormControl('', {validators: [Validators.maxLength(500)]}),
  });

  boundData: CustomDialogData | undefined;
  uploadedFiles: File[] = [];
  isLoading: boolean = false;
  instance: DynamicDialog | undefined;
  countries = countries;
  fileError: string = '';
  maxFileSize: number = 2000000; // 2MB
  allowedFileTypes: string[] = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  logoPreview: string | null = null;

  constructor(private service: BankService, private dialogService: DialogService, private toastService: ToastService,
              private ref: DynamicDialogRef) {
    this.instance = this.dialogService.getInstance(this.ref);
  }

  ngOnInit() {
    this.boundData = this.instance?.data;
    console.log('BankDetailsComponent - boundData:', this.boundData);
    if ((this.boundData?.isEdit || this.boundData?.isView) && this.boundData.data) {
      this.formGroup.patchValue(this.boundData.data);
      // Charger le logo existant si disponible
      if (this.boundData.data.logoUrl) {
        this.logoPreview = this.boundData.data.logoUrl;
      }
    }
    if (this.boundData?.isView) this.formGroup.disable();
  }

  onUpload(event: FileSelectEvent) {
    this.fileError = '';
    this.logoPreview = null;
    this.uploadedFiles = [];

    for(let file of event.currentFiles) {
      // Validation de la taille du fichier
      if (file.size > this.maxFileSize) {
        this.fileError = `Le fichier est trop volumineux. Taille maximale : ${this.maxFileSize / 1000000}MB`;
        this.toastService.showToast('Fichier invalide', this.fileError, 'error');
        return;
      }

      // Validation du type de fichier
      if (!this.allowedFileTypes.includes(file.type)) {
        this.fileError = 'Format de fichier non autorisé. Formats acceptés : JPG, PNG, GIF, WEBP';
        this.toastService.showToast('Fichier invalide', this.fileError, 'error');
        return;
      }

      this.uploadedFiles.push(file);

      // Générer une prévisualisation de l'image
      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.logoPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    }
  }

  /**
   * Validateur personnalisé pour le téléphone
   */
  private phoneValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;

    const phoneStr = control.value.toString().trim();

    // Vérifier que le numéro ne contient que des chiffres, espaces, + et -
    if (!/^[\+\d\s\-]+$/.test(phoneStr)) {
      return { phoneInvalid: true };
    }

    // Extraire uniquement les chiffres
    const digitsOnly = phoneStr.replace(/[\s\-\+]/g, '');

    // Vérifier qu'il y a entre 8 et 15 chiffres
    if (digitsOnly.length < 8 || digitsOnly.length > 15) {
      return { phoneLength: true };
    }

    return null;
  }

  /**
   * Récupère le message d'erreur pour un champ donné
   */
  getFieldError(fieldName: string): string {
    const control = this.formGroup.get(fieldName);
    if (!control || !control.errors || !control.touched) {
      return '';
    }

    const errors = control.errors;
    if (errors['required']) return 'Ce champ est requis';
    if (errors['email']) return 'Adresse email invalide';
    if (errors['minlength']) return `Minimum ${errors['minlength'].requiredLength} caractères`;
    if (errors['maxlength']) return `Maximum ${errors['maxlength'].requiredLength} caractères`;
    if (errors['pattern'] && fieldName === 'swiftCode') return 'Format SWIFT invalide (ex: BNPAFRPP ou BNPAFRPPXXX)';
    if (errors['phoneInvalid']) return 'Le numéro doit contenir uniquement des chiffres, espaces, + et -';
    if (errors['phoneLength']) return 'Le numéro doit contenir entre 8 et 15 chiffres';

    return 'Valeur invalide';
  }

  /**
   * Vérifie si un champ a une erreur et a été touché
   */
  hasFieldError(fieldName: string): boolean {
    const control = this.formGroup.get(fieldName);
    return !!(control && control.errors && control.touched);
  }

  /**
   * Marque tous les champs comme touchés pour afficher les erreurs
   */
  private markAllFieldsAsTouched(): void {
    Object.keys(this.formGroup.controls).forEach(key => {
      const control = this.formGroup.get(key);
      control?.markAsTouched();
    });
  }

  /**
   * Gère les erreurs HTTP et affiche un message approprié
   */
  private handleHttpError(error: any, operation: string): void {
    let errorMessage = 'Une erreur est survenue. Veuillez réessayer.';

    if (error.error) {
      if (typeof error.error === 'string') {
        errorMessage = error.error;
      } else if (error.error.message) {
        errorMessage = error.error.message;
      } else if (error.error.error) {
        errorMessage = error.error.error;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    // Messages d'erreur spécifiques selon le code HTTP
    if (error.status === 400) {
      errorMessage = 'Données invalides. ' + errorMessage;
    } else if (error.status === 401) {
      errorMessage = 'Non autorisé. Veuillez vous reconnecter.';
    } else if (error.status === 403) {
      errorMessage = 'Accès refusé. Vous n\'avez pas les permissions nécessaires.';
    } else if (error.status === 404) {
      errorMessage = 'Banque non trouvée.';
    } else if (error.status === 409) {
      errorMessage = 'Conflit. Une banque avec ce code existe déjà.';
    } else if (error.status === 500) {
      errorMessage = 'Erreur serveur. Veuillez contacter l\'administrateur.';
    } else if (error.status === 0) {
      errorMessage = 'Impossible de contacter le serveur. Vérifiez votre connexion.';
    }

    this.toastService.showToast(
      `Erreur lors de la ${operation}`,
      errorMessage,
      'error'
    );
  }

  onLogoUpdate(event: FileSelectEvent) {
    this.onUpload(event);
    this.service.updateLogo(this.boundData?.data.id, this.uploadedFiles[0]).subscribe({
      next: (bank) => {
        this.toastService.showToast('Logo mis à jour', `Le logo de ${bank.name} a été mis à jour avec succès.`);
        if (this.boundData) this.boundData.data = bank;
      },
      error: (err) => {
        this.handleHttpError(err, 'mise à jour du logo');
      }
    });
  }

  create() {
    // Validation du formulaire avant soumission
    if (this.formGroup.invalid) {
      this.markAllFieldsAsTouched();
      this.toastService.showToast(
        'Formulaire invalide',
        'Veuillez corriger les erreurs avant de soumettre le formulaire',
        'warn'
      );
      return;
    }

    // Validation du fichier logo (obligatoire lors de la création)
    if (!this.uploadedFiles || this.uploadedFiles.length === 0) {
      this.toastService.showToast(
        'Logo manquant',
        'Veuillez ajouter un logo pour la banque',
        'warn'
      );
      return;
    }

    this.isLoading = true;
    const bankDto = this.formGroup.value as IBank;
    this.service.createBank(bankDto, this.uploadedFiles[0]).subscribe({
      next: (bank) => {
        this.toastService.showToast('Banque créée', `La banque ${bank.name} a été créée avec succès.`);
        this.isLoading = false;
        this.ref.close();
      },
      error: (error) => {
        this.isLoading = false;
        this.handleHttpError(error, 'création');
      }
    });
  }

  update() {
    // Validation du formulaire avant soumission
    if (this.formGroup.invalid) {
      this.markAllFieldsAsTouched();
      this.toastService.showToast(
        'Formulaire invalide',
        'Veuillez corriger les erreurs avant de soumettre le formulaire',
        'warn'
      );
      return;
    }

    this.isLoading = true;
    const bankDto = this.formGroup.value as IBank;
    this.service.updateBank(this.boundData!.data.id, bankDto).subscribe({
      next: (bank) => {
        this.toastService.showToast('Banque modifiée', `La banque ${bank.name} a été mise à jour avec succès.`);
        this.isLoading = false;
        this.ref.close();
      },
      error: (error) => {
        this.isLoading = false;
        this.handleHttpError(error, 'modification');
      }
    });
  }
}
